import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

type UseLoopingCarouselOptions = {
  itemCount: number;
  autoMs: number;
  inView: boolean;
  reduceMotion: boolean;
};

const normalizeIndex = (position: number, itemCount: number) => {
  const looped = ((position % itemCount) + itemCount) % itemCount;
  return itemCount + looped;
};

export const useLoopingCarousel = ({
  itemCount,
  autoMs,
  inView,
  reduceMotion,
}: UseLoopingCarouselOptions) => {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [paused, setPaused] = useState(false);
  const [tick, setTick] = useState(0);
  const [step, setStep] = useState(0);
  const [position, setPosition] = useState(itemCount);
  const [transitionEnabled, setTransitionEnabled] = useState(!reduceMotion);

  const measure = useCallback(() => {
    const track = trackRef.current;
    const firstItem = track?.querySelector<HTMLElement>("[data-loop-item]");
    if (!track || !firstItem) return;

    const styles = window.getComputedStyle(track);
    const gapValue = styles.columnGap !== "normal" ? styles.columnGap : styles.gap;
    const gap = Number.parseFloat(gapValue || "0") || 0;
    // Use offsetWidth (layout width, ignores CSS transforms like scale())
    // so the step stays correct even when the active card is scaled up
    // and inactive cards are scaled down.
    const nextStep = firstItem.offsetWidth + gap;
    setStep(nextStep);
  }, []);

  useLayoutEffect(() => {
    setPosition(itemCount);
  }, [itemCount]);

  useLayoutEffect(() => {
    measure();

    const viewport = viewportRef.current;
    if (typeof ResizeObserver === "undefined" || !viewport) {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }

    const observer = new ResizeObserver(() => measure());
    observer.observe(viewport);

    return () => observer.disconnect();
  }, [measure]);

  useEffect(() => {
    setTransitionEnabled(!reduceMotion);
  }, [reduceMotion]);

  useEffect(() => {
    if (!itemCount) return;
    if (!reduceMotion && transitionEnabled) return;

    const normalized = normalizeIndex(position, itemCount);
    if (normalized !== position) {
      setPosition(normalized);
    }
  }, [itemCount, position, reduceMotion, transitionEnabled]);

  useEffect(() => {
    if (reduceMotion || !inView || paused || !itemCount) return;

    const id = window.setInterval(() => {
      setTransitionEnabled(true);
      setPosition((current) => current + 1);
      setTick((current) => current + 1);
    }, autoMs);

    return () => window.clearInterval(id);
    // `tick` is intentionally in deps so the countdown restarts whenever the
    // user manually advances (advance() bumps tick), instead of firing on the
    // remainder of the previous interval.
  }, [autoMs, inView, itemCount, paused, reduceMotion, tick]);

  const advance = useCallback(
    (dir: 1 | -1) => {
      if (!itemCount) return;
      setTransitionEnabled(!reduceMotion);
      setPosition((current) => current + dir);
      setTick((current) => current + 1);
    },
    [itemCount, reduceMotion]
  );

  const handleTransitionEnd = useCallback(() => {
    if (!itemCount || reduceMotion) return;
    if (position < itemCount || position >= itemCount * 2) {
      const normalized = normalizeIndex(position, itemCount);
      setTransitionEnabled(false);
      setPosition(normalized);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTransitionEnabled(true);
        });
      });
    }
  }, [itemCount, position, reduceMotion]);

  const activeIndex = itemCount
    ? ((position % itemCount) + itemCount) % itemCount
    : 0;

  return {
    viewportRef,
    trackRef,
    paused,
    setPaused,
    tick,
    position,
    activeIndex,
    autoActive: inView && !paused && !reduceMotion,
    advance,
    handleTransitionEnd,
    trackStyle: {
      transform: step ? `translate3d(${-position * step}px, 0, 0)` : undefined,
      transition: transitionEnabled ? "transform 650ms cubic-bezier(0.22, 1, 0.36, 1)" : "none",
      visibility: step ? "visible" : "hidden",
    } as const,
  };
};